// Exact account counting for the StackSets target picker.
//
// The picker holds only the levels the user expanded, so the blast-radius number
// cannot be read off a materialised tree any more. This walks ONLY the selected
// subtrees through the same one-level endpoint, and writes every level it fetches
// back into the picker's cache so expanding a folder by hand never refetches it.
//
// Serial on purpose: AWS Organizations sustains ~2.2 calls/sec per account, and a
// bounded 8-way pool measured SLOWER than serial (13.59s vs 9.27s for 20 calls)
// with TooManyRequestsException on 4 of 24 calls. Concurrency here buys nothing
// and costs throttling.

/**
 * @param {string[]} selectedIds OU (or root) ids the user selected
 * @param {object} opts
 * @param {(id: string) => Promise<Array>} opts.getChildren fetches one level
 * @param {Object<string, Array>} [opts.cache] shared parentId -> children cache,
 *   MUTATED IN PLACE — the picker holds this same object, so every level this
 *   walk fetches is visible to it without a refetch
 * @param {() => boolean} [opts.isCancelled] abandons a stale walk
 * @param {(id: string) => void} [opts.onLevel] called after each level resolves,
 *   including a level served from the cache, not only a freshly fetched one
 * @returns {Promise<{cancelled: boolean, accounts: string[]}>}
 */
export async function countAccountsUnderOUs(selectedIds, opts) {
  const { getChildren, cache = {}, isCancelled = () => false, onLevel } = opts;

  const accounts = new Set();
  const visited = new Set();
  const queue = [...(selectedIds || [])];

  while (queue.length > 0) {
    if (isCancelled()) return { cancelled: true, accounts: [] };

    const nodeId = queue.shift();
    if (!nodeId || visited.has(nodeId)) continue;
    visited.add(nodeId);

    let children = cache[nodeId];
    if (!children) {
      const fetched = await getChildren(nodeId);
      if (fetched != null && !Array.isArray(fetched)) {
        throw new Error(`organization hierarchy returned a non-list level for ${nodeId}`);
      }
      children = fetched || [];
      cache[nodeId] = children;
    }
    if (onLevel) onLevel(nodeId);

    (children || []).forEach((child) => {
      if (!child || !child.id) {
        // A malformed node is NOT skippable: if it is an OU, skipping it drops
        // its whole subtree and the walk still reports success. A count that is
        // silently too low is the one outcome this function must never produce,
        // so a bad payload fails the walk and the UI reports "unavailable".
        throw new Error('organization hierarchy returned a node with no id');
      }
      if (child.type === 'ACCOUNT') {
        // Parity with the server: ssCollectActiveAccounts takes ACTIVE (or an
        // absent status) and skips suspended accounts.
        if (!child.status || child.status === 'ACTIVE') accounts.add(child.id);
        return;
      }
      queue.push(child.id);
    });
  }

  return { cancelled: false, accounts: [...accounts] };
}
